import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { usePayRateByPosition } from 'queries/pulse/usePayRateByPosition'
import { PayRateByPosition as PayRateByPositionType } from 'api/pulse/payRateByPosition'
import { formatCurrency, formatNumber, formatPercentage } from 'lib/string'
import { InfiniteTable } from '../InfiniteTable'
import { usePayRateByPositionDownloadCsv } from 'queries/pulse/usePayRateByPositionDownloadCsv'

const columnHelper = createColumnHelper<PayRateByPositionType[0]>()

const columns = [
  columnHelper.accessor('pay_rate', {
    header: 'Pay Rate',
  }),
  columnHelper.accessor('position_type', {
    header: 'Position Type',
  }),
  columnHelper.accessor('avg_market_pay_rate', {
    header: 'Avg Market Pay Rate',
    cell: (info) => (
      <Text>
        {info.getValue() ? formatCurrency(info.getValue() as number) : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('location_count', {
    header: '# Locations',
    cell: (info) => <Text>{formatNumber(info.getValue() as number)}</Text>,
  }),
  columnHelper.accessor('filled_shifts', {
    header: '# Filled Shifts',
    cell: (info) => <Text>{formatNumber(info.getValue() as number)}</Text>,
  }),
  columnHelper.accessor('fill_rate', {
    header: 'Fill Rate',
    cell: (info) => (
      <Text>
        {info.getValue() ? formatPercentage(info.getValue() as number) : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('repeat_worker_rate', {
    header: 'Repeat Worker Rate',
    cell: (info) => (
      <Text>
        {info.getValue() ? formatPercentage(info.getValue() as number) : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('reliability_rate', {
    header: 'Reliability Rate',
    cell: (info) => (
      <Text>
        {info.getValue() ? formatPercentage(info.getValue() as number) : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('avg_worker_to_manager_rating', {
    header: 'Avg Worker to Shift Rating',
    cell: (info) => (
      <Text>
        {info.getValue() ? formatNumber(info.getValue() as number, 2) : 'N/A'}
      </Text>
    ),
  }),
]

export function PayRateByPosition() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    usePayRateByPosition(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    usePayRateByPositionDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Fill by Pay Rate & Location Count
          </Text>
          <Text fontSize={2} color="lightText">
            Fill Metrics by Pay Rate Binning, grouped by position type
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          columns={columns}
          data={flatData}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
